import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  ModalTrigger,
  PrimaryButton,
  SectionMessage,
  Stack,
  Text,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { ApplicantMessageContext } from "@certn/utils";
import { ControlProps } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

type ModalContent = {
  sub_heading: string;
  message: string;
};

export function TextWithLinkModalControl(props: ControlProps) {
  const { t } = useTranslation();
  const isPDF = useContext(RenderContext).isForPdfGeneration;
  const options = props.uischema.options || {};
  const mainBodyText = options?.main_body_text;
  const linkText = options?.link_text;
  const modalMainHeading = options?.modal_content?.main_heading;
  const modalContent = options?.modal_content;
  const testId = options?.test_id || "text-link-with-modal";

  const showApplicantOnlyMessages = useContext(
    ApplicantMessageContext
  ).showApplicantMessages;

  if (!mainBodyText || !linkText || !modalMainHeading || !modalContent) {
    return (
      <Stack>
        <SectionMessage
          icon="error"
          sectionTitle="There are missing keys"
          type="danger"
        >
          The following must be provided to options:
          <ul>
            <li>main_body_text</li>
            <li>link_text</li>
            <li>modal_content</li>
            <li>modal_content.main_heading</li>
          </ul>
        </SectionMessage>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text
        style="form/body-secondary"
        color="text-base"
        testID={`${testId}-main-body-text`}
      >
        {mainBodyText}{" "}
        {!isPDF && (
          <ModalTrigger
            trigger={
              <>
                <Link
                  testID={`${testId}-paragraph-with-link-modal`}
                  linkStyle="monochrome"
                  textStyle="form/body-secondary-link"
                  textColor="text-base"
                >
                  {linkText}
                </Link>
              </>
            }
          >
            <Modal>
              <Dialog>
                {({ close }) => (
                  <>
                    <DialogHeader>{modalMainHeading}</DialogHeader>
                    <DialogContent>
                      {Array.isArray(modalContent?.content) && (
                        <Stack gap="space-4">
                          {modalContent.content.map(
                            (item: ModalContent, i: number) => {
                              return (
                                <div key={i}>
                                  {item.sub_heading && (
                                    <Text
                                      as="h4"
                                      color="text-base"
                                      style="global/heading-medium"
                                    >
                                      {item.sub_heading}
                                    </Text>
                                  )}
                                  {item.message && (
                                    <Markdown
                                      components={{
                                        p: (props) => {
                                          return (
                                            <Text
                                              color="text-base"
                                              style="form/body-secondary"
                                              marginTop="space-3"
                                              as="p"
                                            >
                                              {props.children}
                                            </Text>
                                          );
                                        },
                                        a: (props) => {
                                          return (
                                            <Link
                                              href={props.href}
                                              target="_blank"
                                              testID="markdown-link"
                                              linkStyle="monochrome"
                                            >
                                              {props.children}
                                            </Link>
                                          );
                                        },
                                      }}
                                    >
                                      {item.message}
                                    </Markdown>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </Stack>
                      )}
                      {typeof modalContent?.content === "string" && (
                        <Text>{modalContent?.content}</Text>
                      )}
                      {showApplicantOnlyMessages &&
                        options.modal_content?.applicant_only_footer && (
                          <Text
                            color="text-base"
                            style="form/body-secondary"
                            marginTop="space-3"
                            as="p"
                          >
                            {options.modal_content?.applicant_only_footer}
                          </Text>
                        )}
                    </DialogContent>
                    <DialogFooter>
                      <PrimaryButton onPress={close}>
                        {t("Close")}
                      </PrimaryButton>
                    </DialogFooter>
                  </>
                )}
              </Dialog>
            </Modal>
          </ModalTrigger>
        )}
      </Text>
    </Stack>
  );
}

export const TextWithLinkModalControlRenderer = withJsonFormsControlProps(
  TextWithLinkModalControl
);
